<template>
  <div class="grade-page" v-loading="pageLoading">
    <!-- 顶栏 -->
    <header class="grade-header">
      <div class="grade-header__left">
        <el-button type="text" icon="el-icon-arrow-left" class="back-btn" @click="goBack">{{ $route.query.from === 'inspectionJob' ? '鉴别作业' : '寄售订单' }}</el-button>
        <span class="grade-header__divider">/</span>
        <span class="grade-header__title">寄售评级</span>
        <el-tag v-if="listed" type="success" size="mini" effect="plain">已上架</el-tag>
        <el-tag v-else-if="gradeCompleted" type="warning" size="mini" effect="plain">待用户上架</el-tag>
      </div>
      <div class="grade-header__score" v-if="displayGradeLevel">
        <span class="grade-badge">{{ displayGradeLevel }}</span>
        <span class="grade-score">{{ preview.compositeScore != null ? preview.compositeScore : "—" }}<small>/10</small></span>
      </div>
    </header>

    <!-- 订单摘要 -->
    <div class="order-bar" v-if="order">
      <span class="order-bar__item"><label>订单</label>{{ order.orderId || order.orderid }}</span>
      <span class="order-bar__item"><label>品牌</label>{{ order.brandName || "—" }}</span>
      <span class="order-bar__item"><label>型号</label>{{ order.typeName || "—" }}</span>
      <span class="order-bar__item"><label>寄售价</label>¥{{ order.jsPrice || "—" }}</span>
      <span class="order-bar__item order-bar__item--muted">标准 {{ standardVersion }}</span>
    </div>

    <el-alert
      v-if="order && order.jbjg === 0"
      type="error"
      :closable="false"
      title="鉴别为假，不可评级上架"
      show-icon
      class="alert-block"
    />

    <el-alert
      v-else-if="gradeCompleted"
      type="info"
      :closable="false"
      title="评级已完成，寄售用户可在「我的寄售 - 待上架」填写售价并确认上架"
      show-icon
      class="alert-block"
    />

    <el-alert
      v-else-if="listed"
      type="success"
      :closable="false"
      title="该寄售单已上架，可在「闲置商品」中查看或编辑"
      show-icon
      class="alert-block"
    />

    <div class="grade-layout">
      <!-- 主内容：分步 -->
      <main class="grade-main">
        <el-steps :active="stepIndex" finish-status="success" align-center class="grade-steps">
          <el-step title="成色评级" description="勾选缺陷并补充说明"></el-step>
          <el-step title="商品描述" description="确认生成的上架文案"></el-step>
        </el-steps>

        <!-- Step 1: 评级 -->
        <section v-show="activeStep === 'grade'" class="step-panel">
          <div class="ref-images-row">
            <div class="defect-panel ref-images-col">
              <div class="defect-panel__head">
                <span class="panel-title">瑕疵图</span>
                <span class="defect-panel__hint">对照勾选扣分项</span>
              </div>
              <div v-if="defectImages.length" class="img-grid img-grid--readonly">
                <div
                  v-for="(url, idx) in defectImages"
                  :key="'defect-' + idx"
                  class="img-thumb"
                  @click="previewImage(defectImages, idx, '瑕疵图')"
                >
                  <img :src="url" alt="" />
                </div>
              </div>
              <el-empty
                v-else
                :image-size="56"
                description="暂无瑕疵图"
              />
            </div>

            <div class="defect-panel ref-images-col">
              <div class="defect-panel__head">
                <span class="panel-title">细节图</span>
                <span class="defect-panel__hint">实物图 / 商品图</span>
              </div>
              <div v-if="detailImages.length" class="img-grid img-grid--readonly">
                <div
                  v-for="(url, idx) in detailImages"
                  :key="'detail-' + idx"
                  class="img-thumb"
                  @click="previewImage(detailImages, idx, '细节图')"
                >
                  <img :src="url" alt="" />
                </div>
              </div>
              <el-empty
                v-else
                :image-size="56"
                description="暂无细节图"
              />
            </div>
          </div>

          <div class="panel-head">
            <div>
              <div class="panel-title">成色评级</div>
              <p class="panel-desc">按维度勾选缺陷，右侧实时显示综合分；每类扣分有上限</p>
            </div>
            <div class="panel-head__stat">
              已选 <strong>{{ selectedCount }}</strong> 项
            </div>
          </div>

          <el-tabs v-model="activeCategory" type="border-card" class="cat-tabs">
            <el-tab-pane
              v-for="cat in categories"
              :key="cat.key"
              :name="cat.key"
            >
              <span slot="label">
                {{ cat.name }}(上限{{ cat.maxDeduction }}分)
                <el-badge
                  v-if="categorySelectedCount(cat.key) > 0"
                  :value="categorySelectedCount(cat.key)"
                  class="cat-badge"
                />
              </span>
              <div class="cat-meta">
                {{ cat.name }}（上限 <b>{{ cat.maxDeduction }}</b> 分）
                <span v-if="categoryCappedDeduction(cat.key) > 0" class="cat-deduct">
                  · 本类已扣 {{ categoryCappedDeduction(cat.key) }} 分
                </span>
              </div>
              <el-table
                :data="cat.items || []"
                size="small"
                border
                stripe
                class="defect-table"
                style="width: 100%"
                :row-class-name="defectRowClass(cat.key)"
              >
                <el-table-column width="52" align="center">
                  <template slot-scope="scope">
                    <el-checkbox
                      v-model="selectionMap[cat.key + '::' + scope.row.key].checked"
                      :disabled="listed"
                      @change="onSelectionChange"
                    />
                  </template>
                </el-table-column>
                <el-table-column prop="name" label="项目" min-width="120" show-overflow-tooltip />
                <el-table-column label="瑕疵类型" width="88" align="center">
                  <template slot-scope="scope">
                    <span class="type-label">{{ defectTypeLabel(scope.row) }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="扣分标准" width="88" align="center">
                  <template slot-scope="scope">
                    <span class="deduct-val">{{ scope.row.deduction }}分</span>
                  </template>
                </el-table-column>
                <el-table-column label="数量" width="140" align="center">
                  <template slot-scope="scope">
                    <el-input-number
                      v-if="scope.row.deductionType === 'QUANTIFIED'"
                      v-model="selectionMap[cat.key + '::' + scope.row.key].quantity"
                      :min="1"
                      :max="99"
                      size="mini"
                      controls-position="right"
                      :disabled="listed || !selectionMap[cat.key + '::' + scope.row.key].checked"
                      @change="onSelectionChange"
                    />
                    <span v-else class="text-muted">—</span>
                  </template>
                </el-table-column>
              </el-table>
            </el-tab-pane>
          </el-tabs>

          <div class="extra-mobile">
            <div class="aside-extra__title">补充说明（成色 / 余量 / 剩余保质期）</div>
            <div class="attr-form">
              <div class="attr-row">
                <span class="attr-label">成色</span>
                <div class="attr-tags">
                  <span
                    v-for="c in conditionList"
                    :key="'m-cond-' + c.value"
                    class="attr-tag"
                    :class="{ 'attr-tag--active': form.conditionLevel === c.value, 'is-disabled': listed }"
                    @click="toggleConditionLevel(c.value)"
                  >{{ c.label }}</span>
                </div>
              </div>
              <div class="attr-row">
                <span class="attr-label">余量</span>
                <div class="attr-tags">
                  <span
                    v-for="o in remainingListForUi"
                    :key="'m-rm-' + o.value"
                    class="attr-tag"
                    :class="{ 'attr-tag--active': form.remainingAmount === o.value, 'is-disabled': listed }"
                    @click="setAttrExtra('remainingAmount', o.value)"
                  >{{ o.label }}</span>
                </div>
              </div>
              <div class="attr-row">
                <span class="attr-label">剩余保质期</span>
                <div class="attr-tags">
                  <span
                    v-for="o in shelfLifeList"
                    :key="'m-sl-' + o.value"
                    class="attr-tag"
                    :class="{ 'attr-tag--active': form.shelfLife === o.value, 'is-disabled': listed }"
                    @click="setAttrExtra('shelfLife', o.value)"
                  >{{ o.label }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="step-actions">
            <el-button type="primary" :loading="saving" :disabled="listed" @click="saveAndNext">
              下一步：查看商品描述
            </el-button>
          </div>
        </section>

        <!-- Step 2: 商品描述 -->
        <section v-show="activeStep === 'desc'" class="step-panel">
          <div class="panel-head">
            <div>
              <div class="panel-title">商品描述</div>
              <p class="panel-desc">根据评级结果自动生成正文，可手动修改；固定话术由系统变量维护，展示时自动追加</p>
            </div>
            <div class="panel-head__stat desc-grade-tag">
              <span class="grade-badge">{{ displayGradeLevel || "—" }}</span>
              <span>{{ preview.compositeScore != null ? preview.compositeScore : "—" }}/10</span>
            </div>
          </div>

          <div class="listing-desc-preview listing-desc-preview--step">
            <el-input
              type="textarea"
              :rows="12"
              v-model="listingDescription"
              :disabled="listed"
              placeholder="完成评级后将自动生成描述正文，也可在此直接编辑（不含下方固定话术）"
            />
          </div>

          <div class="listing-desc-footer-block" v-if="listingDescriptionFooter">
            <div class="listing-desc-footer-block__label">固定话术（系统变量，展示时自动追加，不写入数据库）</div>
            <pre class="listing-desc-footer-block__text">{{ listingDescriptionFooter }}</pre>
          </div>

          <div class="listing-desc-footer-block listing-desc-footer-block--preview" v-if="listingDescriptionDisplay">
            <div class="listing-desc-footer-block__label">买家可见完整描述预览</div>
            <pre class="listing-desc-footer-block__text">{{ listingDescriptionDisplay }}</pre>
          </div>

          <div class="step-actions">
            <el-button :disabled="listed" @click="goStep('grade')">上一步</el-button>
            <el-button type="primary" plain :loading="saving" :disabled="listed" @click="saveGrade(false)">
              更新评级与描述
            </el-button>
            <el-button type="primary" @click="finishGrade">完成</el-button>
          </div>
        </section>
      </main>

      <!-- 右侧摘要（桌面端） -->
      <aside class="grade-aside" v-if="!listed && activeStep === 'grade'">
        <div class="aside-card">
          <div class="aside-extra">
            <div class="aside-extra__title">补充说明（成色 / 余量 / 剩余保质期）</div>
            <div class="attr-form attr-form--aside">
              <div class="attr-row">
                <span class="attr-label">成色</span>
                <div class="attr-tags">
                  <span
                    v-for="c in conditionList"
                    :key="'cond-' + c.value"
                    class="attr-tag"
                    :class="{ 'attr-tag--active': form.conditionLevel === c.value, 'is-disabled': listed }"
                    @click="toggleConditionLevel(c.value)"
                  >{{ c.label }}</span>
                </div>
              </div>
              <div class="attr-row">
                <span class="attr-label">余量</span>
                <div class="attr-tags">
                  <span
                    v-for="o in remainingListForUi"
                    :key="'rm-' + o.value"
                    class="attr-tag"
                    :class="{ 'attr-tag--active': form.remainingAmount === o.value, 'is-disabled': listed }"
                    @click="setAttrExtra('remainingAmount', o.value)"
                  >{{ o.label }}</span>
                </div>
              </div>
              <div class="attr-row">
                <span class="attr-label">剩余保质期</span>
                <div class="attr-tags">
                  <span
                    v-for="o in shelfLifeList"
                    :key="'sl-' + o.value"
                    class="attr-tag"
                    :class="{ 'attr-tag--active': form.shelfLife === o.value, 'is-disabled': listed }"
                    @click="setAttrExtra('shelfLife', o.value)"
                  >{{ o.label }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="aside-score">
            <div class="aside-score__level">{{ displayGradeLevel || "待评" }}</div>
            <div class="aside-score__num">
              {{ preview.compositeScore != null ? preview.compositeScore : "—" }}
              <small>/ 10</small>
            </div>
          </div>
          <ul class="aside-summary" v-if="selectedSummary.length">
            <li v-for="(line, i) in selectedSummary.slice(0, 6)" :key="i">{{ line }}</li>
            <li v-if="selectedSummary.length > 6" class="aside-summary__more">还有 {{ selectedSummary.length - 6 }} 项…</li>
          </ul>
          <p v-else class="aside-empty">暂未勾选缺陷，默认 SS 级（10 分）</p>
          <div class="aside-actions">
            <el-button size="small" type="primary" :loading="saving" @click="saveAndNext">
              下一步：查看描述
            </el-button>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
const CONDITION_LIST = [
  { label: "全新带塑封", value: 1 },
  { label: "正装盒", value: 2 },
  { label: "简装盒", value: 3 },
  { label: "无盒", value: 4 }
];
const REMAINING_LIST = [
  { label: "满余量", value: "full" },
  { label: "仅试喷", value: "try" },
  { label: "0-1/3", value: "0_1_3" },
  { label: "1/3-2/3", value: "1_3_2_3" },
  { label: "2/3-1", value: "2_3_1" }
];
const SHELF_LIFE_LIST = [
  { label: "1年以上", value: "y1" },
  { label: "0-12个月", value: "m0_12" },
  { label: "已过期", value: "expired" },
  { label: "不详", value: "unknown" }
];
const REMAINING_LABEL_TO_VALUE = REMAINING_LIST.reduce((m, o) => { m[o.label] = o.value; return m; }, {});
const SHELF_LIFE_LABEL_TO_VALUE = SHELF_LIFE_LIST.reduce((m, o) => { m[o.label] = o.value; return m; }, {});

export default {
  name: "ConsignmentGrade",
  data() {
    return {
      pageLoading: false,
      orderId: null,
      order: null,
      listed: false,
      activeStep: "grade",
      activeCategory: "box",
      standardVersion: "v25.12.2",
      conditionList: CONDITION_LIST,
      remainingList: REMAINING_LIST,
      shelfLifeList: SHELF_LIFE_LIST,
      categories: [],
      selectionMap: {},
      defectImages: [],
      detailImages: [],
      preview: { compositeScore: null, gradeLevel: "" },
      previewDetail: null,
      listingDescription: "",
      listingDescriptionFooter: "",
      form: {
        conditionLevel: null,
        remainingAmount: "",
        shelfLife: ""
      },
      saving: false,
      previewTimer: null
    };
  },
  computed: {
    stepIndex() {
      return this.activeStep === "desc" ? 1 : 0;
    },
    gradeCompleted() {
      return !!this.displayGradeLevel && !this.listed;
    },
    /** 满分 10 分为 SS；「全新」仅指包装成色，非评级等级 */
    displayGradeLevel() {
      return this.normalizeGradeLevel(this.preview.gradeLevel);
    },
    selectedCount() {
      return Object.keys(this.selectionMap).filter((k) => this.selectionMap[k] && this.selectionMap[k].checked).length;
    },
    selectedSummary() {
      const lines = [];
      if (!this.previewDetail || !this.previewDetail.deductions) return lines;
      (this.previewDetail.deductions || []).forEach((d) => {
        const qty = d.quantity > 1 ? " ×" + d.quantity : "";
        lines.push((d.categoryName || "") + " · " + (d.itemName || "") + qty);
      });
      return lines;
    },
    /** 全新带塑封时仅展示「满余量」 */
    remainingListForUi() {
      if (Number(this.form.conditionLevel) === 1) {
        return [{ label: "满余量", value: "full" }];
      }
      return this.remainingList;
    },
    listingDescriptionDisplay() {
      const body = (this.listingDescription || "").trim();
      const footer = (this.listingDescriptionFooter || "").trim();
      if (!body) return footer;
      if (!footer) return body;
      return body + "\n\n" + footer;
    }
  },
  created() {
    this.orderId = this.$route.query.orderId ? Number(this.$route.query.orderId) : null;
    if (!this.orderId) {
      this.$message.error("缺少订单ID");
      return;
    }
    this.loadAll();
  },
  methods: {
    goBack() {
      if (this.$route.query.from === "inspectionJob") {
        this.$router.push("/inspectionJob/index");
        return;
      }
      this.$router.push("/goodsOrder/consignmentOrder");
    },
    goStep(step) {
      this.activeStep = step;
    },
    finishGrade() {
      this.$message.success("评级流程已完成");
      this.goBack();
    },
    parseOrderImages(order) {
      if (!order || !order.images) return [];
      const raw = order.images;
      if (Array.isArray(raw)) return raw.filter(Boolean);
      if (typeof raw === "string") {
        const t = raw.trim();
        if (t.startsWith("[")) {
          try {
            return JSON.parse(t).filter(Boolean);
          } catch (e) {
            return [];
          }
        }
        return t.split(",").map((s) => s.trim()).filter(Boolean);
      }
      return [];
    },
    previewImage(list, index, title) {
      if (!list || !list.length) return;
      this.$alert(
        `<img src="${list[index]}" style="max-width:100%;border-radius:6px;" />`,
        (title || "图片") + "预览",
        { dangerouslyUseHTMLString: true, customClass: "defect-preview-dialog" }
      );
    },
    /** 瑕疵类型：原型为「每处」或「—」；兼容接口未返回 typeLabel 的情况 */
    defectTypeLabel(row) {
      if (!row) return "—";
      const label = row.typeLabel || row.type_label;
      if (label) return label;
      const dt = row.deductionType || row.deduction_type;
      if (dt === "QUANTIFIED") return "每处";
      return "—";
    },
    categorySelectedCount(catKey) {
      let n = 0;
      Object.keys(this.selectionMap).forEach((k) => {
        const s = this.selectionMap[k];
        if (s && s.checked && s.categoryKey === catKey) n++;
      });
      return n;
    },
    categoryCappedDeduction(catKey) {
      if (!this.previewDetail || !this.previewDetail.categorySummary) return 0;
      const row = (this.previewDetail.categorySummary || []).find((c) => c.categoryKey === catKey);
      return row && row.cappedDeduction != null ? row.cappedDeduction : 0;
    },
    defectRowClass(catKey) {
      return ({ row }) => {
        const s = this.selectionMap[catKey + "::" + row.key];
        return s && s.checked ? "defect-row--active" : "";
      };
    },
    loadAll() {
      this.pageLoading = true;
      Promise.all([this.fetchStandard(), this.fetchContext()])
        .catch(() => this.$message.error("加载失败"))
        .finally(() => {
          this.pageLoading = false;
        });
    },
    fetchStandard() {
      return this.$https(this.$api.api.consignmentGradeStandard, {}, "GET").then((res) => {
        const body = res.data || {};
        if (body.code !== 200 || !body.data) return;
        this.standardVersion = body.data.version || this.standardVersion;
        this.categories = body.data.categories || [];
        if (this.categories.length && !this.categories.find((c) => c.key === this.activeCategory)) {
          this.activeCategory = this.categories[0].key;
        }
        this.initSelectionMap();
      });
    },
    fetchContext() {
      return this.$https(this.$api.api.consignmentGradeContext, { orderId: this.orderId }, "GET").then((res) => {
        const body = res.data || {};
        if (body.code !== 200) {
          this.$message.error(body.msg || "加载订单失败");
          return;
        }
        const data = body.data || {};
        this.order = data.order || null;
        this.listed = !!data.listed;
        this.defectImages = this.normalizeImageUrls(
          Array.isArray(data.defectImages) ? data.defectImages : []
        );
        if (!this.defectImages.length && data.inspectionJob && data.inspectionJob.defectImgs) {
          this.defectImages = this.normalizeImageUrls(this.parseImgsRaw(data.inspectionJob.defectImgs));
        }
        this.detailImages = this.normalizeImageUrls(
          Array.isArray(data.detailImages) ? data.detailImages : []
        );
        if (!this.detailImages.length) {
          this.detailImages = this.normalizeImageUrls(this.resolveDetailImagesFromContext(data));
        }
        const job = data.inspectionJob || {};
        this.restoreAttrExtra(data.gradeJson, job);
        if (data.compositeScore != null) this.preview.compositeScore = data.compositeScore;
        this.preview.gradeLevel = this.normalizeGradeLevel(data.gradeLevel
          || (data.gradeJson && data.gradeJson.gradeLevel)
          || "");
        if (data.gradeJson) {
          this.previewDetail = { ...data.gradeJson, gradeLevel: this.normalizeGradeLevel(data.gradeJson.gradeLevel) };
        }
        this.listingDescriptionFooter = data.listingDescriptionFooter || "";
        this.listingDescription = this.normalizeListingDescription(
          data.listingDescription
            || (data.gradeJson && data.gradeJson.listingDescription)
            || "",
          this.preview.gradeLevel,
          this.listingDescriptionFooter
        );
        this.restoreSelections(data.gradeJson);
        if (this.listed) {
          this.activeStep = "desc";
        } else if (this.listingDescription) {
          this.activeStep = "desc";
        } else {
          this.activeStep = "grade";
        }
        this.schedulePreview();
      });
    },
    normalizeGradeLevel(level) {
      if (!level) return "";
      const t = String(level).trim();
      return t === "全新" ? "SS" : t;
    },
    normalizeListingDescription(desc, gradeLevel, footer) {
      if (!desc) return "";
      let text = desc;
      const foot = (footer || this.listingDescriptionFooter || "").trim();
      if (foot) {
        const suffix = "\n\n" + foot;
        if (text.trim().endsWith(suffix)) {
          text = text.trim().slice(0, -suffix.length);
        } else if (text.trim().endsWith(foot)) {
          text = text.trim().slice(0, -foot.length).replace(/\n+$/, "");
        }
      }
      if (this.normalizeGradeLevel(gradeLevel) === "SS") {
        return text.replace(/【评级】全新/g, "【评级】SS");
      }
      return text;
    },
    isLikelyImageUrl(url) {
      if (!url || typeof url !== "string") return false;
      const t = url.trim();
      if (!t || t.startsWith("{") || t.startsWith("[") || t.includes('"img"')) return false;
      return /^https?:\/\//i.test(t) || t.startsWith("/");
    },
    normalizeImageUrls(urls) {
      const seen = new Set();
      return (urls || []).filter((u) => {
        if (!this.isLikelyImageUrl(u)) return false;
        const key = u.trim();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    parseImgsRaw(raw) {
      if (!raw) return [];
      if (Array.isArray(raw)) {
        return raw.filter((u) => this.isLikelyImageUrl(u));
      }
      if (typeof raw === "object") {
        const urls = [];
        ["img", "packImg", "image", "pack_image"].forEach((k) => {
          if (raw[k]) urls.push(raw[k]);
        });
        return urls.filter((u) => this.isLikelyImageUrl(u));
      }
      if (typeof raw === "string") {
        const t = raw.trim();
        if (!t) return [];
        if (t.startsWith("{")) {
          try {
            return this.parseImgsRaw(JSON.parse(t));
          } catch (e) {
            return [];
          }
        }
        if (t.startsWith("[")) {
          try {
            return this.parseImgsRaw(JSON.parse(t));
          } catch (e) {
            return [];
          }
        }
        return t.split(",").map((s) => s.trim()).filter((u) => this.isLikelyImageUrl(u));
      }
      return [];
    },
    resolveDetailImagesFromContext(data) {
      const urls = [];
      const order = (data && data.order) || {};
      const job = (data && data.inspectionJob) || {};
      this.pushRealpicture(urls, order.realpicture);
      this.pushRealpicture(urls, job.realpicture);
      this.pushImgsRaw(urls, order.images);
      this.pushImgsRaw(urls, job.productImage);
      return urls;
    },
    pushRealpicture(target, raw) {
      if (!raw) return;
      let obj = raw;
      if (typeof raw === "string") {
        const t = raw.trim();
        if (!t) return;
        if (t.startsWith("[")) {
          this.pushImgsRaw(target, t);
          return;
        }
        try {
          obj = JSON.parse(t);
        } catch (e) {
          return;
        }
      }
      if (Array.isArray(obj.urls)) {
        obj.urls.forEach((u) => {
          if (this.isLikelyImageUrl(u)) target.push(u.trim());
        });
      }
      ["img", "packImg", "img1", "img2", "img3", "img4"].forEach((k) => {
        if (this.isLikelyImageUrl(obj[k])) target.push(obj[k].trim());
      });
    },
    pushImgsRaw(target, raw) {
      this.parseImgsRaw(raw).forEach((u) => {
        const key = u.trim();
        if (target.indexOf(key) === -1) target.push(key);
      });
    },
    initSelectionMap() {
      const map = {};
      this.categories.forEach((cat) => {
        (cat.items || []).forEach((item) => {
          const k = cat.key + "::" + item.key;
          map[k] = { checked: false, quantity: 1, categoryKey: cat.key, itemKey: item.key };
        });
      });
      this.selectionMap = map;
    },
    restoreAttrExtra(gradeJson, job) {
      this.form.conditionLevel = null;
      this.form.remainingAmount = "";
      this.form.shelfLife = "";
      const extra = gradeJson && gradeJson.attrExtra;
      if (extra && typeof extra === "object") {
        if (extra.conditionLevel != null && extra.conditionLevel !== "") {
          this.form.conditionLevel = Number(extra.conditionLevel);
        }
        if (extra.remainingAmount) this.form.remainingAmount = extra.remainingAmount;
        if (extra.shelfLife) this.form.shelfLife = extra.shelfLife;
        return;
      }
      if (job) {
        if (job.remainDesc && REMAINING_LABEL_TO_VALUE[job.remainDesc]) {
          this.form.remainingAmount = REMAINING_LABEL_TO_VALUE[job.remainDesc];
        }
        if (job.shelfLifeDesc && SHELF_LIFE_LABEL_TO_VALUE[job.shelfLifeDesc]) {
          this.form.shelfLife = SHELF_LIFE_LABEL_TO_VALUE[job.shelfLifeDesc];
        }
      }
    },
    toggleConditionLevel(val) {
      if (this.listed) return;
      const prev = this.form.conditionLevel;
      if (this.form.conditionLevel === val) {
        this.form.conditionLevel = null;
        if (val === 1 && this.form.remainingAmount === "full") {
          this.form.remainingAmount = "";
        }
      } else {
        this.form.conditionLevel = val;
        if (val === 1) {
          this.form.remainingAmount = "full";
        } else if (prev === 1 && this.form.remainingAmount === "full") {
          this.form.remainingAmount = "";
        }
      }
    },
    setAttrExtra(key, val) {
      if (this.listed) return;
      if (key === "remainingAmount" && Number(this.form.conditionLevel) === 1) {
        if (val !== "full") return;
        this.form.remainingAmount = "full";
        return;
      }
      if (this.form[key] === val) {
        this.form[key] = "";
      } else {
        this.form[key] = val;
      }
    },
    labelForCondition(level) {
      const row = this.conditionList.find((c) => c.value === level);
      return row ? row.label : "";
    },
    labelForRemaining(code) {
      const row = this.remainingList.find((o) => o.value === code);
      return row ? row.label : "";
    },
    labelForShelfLife(code) {
      const row = this.shelfLifeList.find((o) => o.value === code);
      return row ? row.label : "";
    },
    buildSubmitExtra() {
      return {
        conditionLevel: this.form.conditionLevel,
        remainingAmount: this.form.remainingAmount || undefined,
        shelfLife: this.form.shelfLife || undefined,
        remainDesc: this.labelForRemaining(this.form.remainingAmount),
        shelfLifeDesc: this.labelForShelfLife(this.form.shelfLife)
      };
    },
    restoreSelections(gradeJson) {
      if (!gradeJson || !gradeJson.deductions) return;
      (gradeJson.deductions || []).forEach((line) => {
        const k = line.categoryKey + "::" + line.itemKey;
        if (this.selectionMap[k]) {
          this.selectionMap[k].checked = true;
          this.selectionMap[k].quantity = line.quantity || 1;
        }
      });
    },
    collectDeductions() {
      const list = [];
      Object.keys(this.selectionMap).forEach((k) => {
        const s = this.selectionMap[k];
        if (s && s.checked) {
          list.push({ categoryKey: s.categoryKey, itemKey: s.itemKey, quantity: s.quantity || 1 });
        }
      });
      return list;
    },
    onSelectionChange() {
      this.schedulePreview();
    },
    schedulePreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => this.runPreview(), 280);
    },
    runPreview() {
      this.$https(this.$api.api.consignmentGradePreview, {
        orderId: this.orderId,
        deductions: this.collectDeductions()
      }, "POST").then((res) => {
        const body = res.data || {};
        if (body.code !== 200 || !body.data) return;
        this.preview.compositeScore = body.data.compositeScore;
        this.preview.gradeLevel = this.normalizeGradeLevel(body.data.gradeLevel || "");
        if (body.data.gradeJson) {
          const gj = typeof body.data.gradeJson === "string"
            ? JSON.parse(body.data.gradeJson) : body.data.gradeJson;
          gj.gradeLevel = this.normalizeGradeLevel(gj.gradeLevel);
          this.previewDetail = gj;
        }
      });
    },
    applyListingDescriptionFromJob(job) {
      if (!job || !job.gradeJson) return;
      try {
        const gj = typeof job.gradeJson === "string" ? JSON.parse(job.gradeJson) : job.gradeJson;
        gj.gradeLevel = this.normalizeGradeLevel(gj.gradeLevel || (job.gradeLevel || ""));
        this.previewDetail = gj;
        this.preview.gradeLevel = gj.gradeLevel;
        this.listingDescription = this.normalizeListingDescription(
          gj.listingDescription || "",
          gj.gradeLevel,
          this.listingDescriptionFooter
        );
      } catch (e) {
        /* ignore */
      }
    },
    saveAndNext() {
      this.saveGrade(true);
    },
    saveGrade(goDesc) {
      if (this.listed) return;
      this.saving = true;
      const payload = {
        orderId: this.orderId,
        deductions: this.collectDeductions(),
        ...this.buildSubmitExtra()
      };
      if (!goDesc && this.listingDescription && this.listingDescription.trim()) {
        payload.listingDescription = this.listingDescription.trim();
      }
      this.$https(this.$api.api.consignmentGradeSubmit, payload, "POST").then((res) => {
        const body = res.data || {};
        if (body.code === 200) {
          this.applyListingDescriptionFromJob(body.data);
          if (goDesc) {
            if (this.listingDescription) {
              this.activeStep = "desc";
              this.$message.success("评级已保存，请确认商品描述");
            } else {
              this.$message.warning("描述生成失败，请重试");
            }
          } else {
            this.$message.success("评级与描述已更新");
          }
        } else {
          this.$message.error(body.msg || "保存失败");
        }
      }).finally(() => { this.saving = false; });
    }
  }
};
</script>

<style lang="less" scoped>
.grade-page {
  min-height: calc(100vh - 80px);
  background: #f0f2f5;
  padding: 0 0 32px;
}

.grade-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  position: sticky;
  top: 0;
  z-index: 10;
}
.grade-header__left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.back-btn {
  padding-left: 0;
  color: #606266;
}
.grade-header__divider {
  color: #dcdfe6;
}
.grade-header__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.grade-header__score {
  display: flex;
  align-items: center;
  gap: 10px;
}
.grade-badge {
  display: inline-block;
  min-width: 36px;
  padding: 4px 10px;
  background: #111;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  border-radius: 4px;
  text-align: center;
}
.grade-score {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  small { font-size: 13px; color: #909399; font-weight: 400; }
}

.order-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  padding: 10px 24px;
  background: #fafafa;
  border-bottom: 1px solid #eee;
  font-size: 13px;
  color: #303133;
}
.order-bar__item label {
  color: #909399;
  margin-right: 6px;
}
.order-bar__item--muted {
  color: #909399;
  margin-left: auto;
}

.alert-block {
  margin: 12px 24px 0;
}

.grade-layout {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 24px 0;
  max-width: 1280px;
}

.grade-main {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 8px;
  padding: 20px 24px 24px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.grade-steps {
  margin-bottom: 28px;
  /deep/ .el-step__title { font-size: 13px; }
  /deep/ .el-step__description { font-size: 12px; }
}

.step-panel { animation: fadeIn 0.2s ease; }
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
}

.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}
.panel-head__stat {
  font-size: 13px;
  color: #606266;
  strong { color: #e6a23c; font-size: 16px; }
}
.panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}
.panel-desc {
  font-size: 12px;
  color: #909399;
  margin: 0 0 16px;
}

.ref-images-row {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.ref-images-col {
  flex: 1;
  min-width: 280px;
  margin-bottom: 0;
}
.defect-panel {
  margin-bottom: 20px;
  padding: 14px 16px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #eee;
}
.defect-panel__head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 12px;
  .panel-title { margin-bottom: 0; }
}
.defect-panel__hint {
  font-size: 12px;
  color: #909399;
}
.img-grid--readonly .img-thumb {
  cursor: zoom-in;
  &:hover { opacity: 0.92; }
}

.img-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.img-thumb {
  position: relative;
  width: 88px;
  height: 88px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #ebeef5;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.img-thumb__tag {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 10px;
  text-align: center;
  line-height: 18px;
}
.img-thumb__del {
  position: absolute;
  top: 4px; right: 4px;
  width: 20px; height: 20px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  cursor: pointer;
  padding: 0;
  line-height: 20px;
  font-size: 12px;
}
.img-add {
  width: 88px;
  height: 88px;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 11px;
  cursor: pointer;
  transition: border-color 0.2s;
  i { font-size: 20px; margin-bottom: 4px; }
  &:hover { border-color: #409eff; color: #409eff; }
  &.is-loading { pointer-events: none; opacity: 0.7; }
}

.cat-tabs {
  margin-bottom: 12px;
  width: 100%;
  /deep/ .el-tabs__content { padding: 12px 0 0; }
  /deep/ .el-tabs__header { margin: 0; }
}
.cat-badge /deep/ .el-badge__content {
  height: 16px;
  line-height: 16px;
  padding: 0 5px;
}
.cat-meta {
  font-size: 12px;
  color: #909399;
  margin-bottom: 10px;
  b { color: #606266; }
}
.cat-deduct { color: #e6a23c; }

.defect-table {
  width: 100%;
  /deep/ .defect-row--active { background: #fdf6ec !important; }
}
.deduct-val {
  color: #e6a23c;
  font-weight: 600;
  font-size: 13px;
}
.type-label {
  color: #606266;
  font-size: 12px;
}
.text-muted { color: #c0c4cc; }

.attr-form {
  padding-top: 4px;
}
.attr-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}
.attr-label {
  flex-shrink: 0;
  width: 72px;
  font-size: 13px;
  color: #606266;
  line-height: 28px;
  text-align: right;
}
.attr-tags {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.attr-tag {
  display: inline-block;
  padding: 4px 12px;
  font-size: 12px;
  color: #606266;
  background: #f4f4f5;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;
  &:hover:not(.is-disabled) {
    border-color: #409eff;
    color: #409eff;
  }
  &--active {
    color: #409eff;
    background: #ecf5ff;
    border-color: #b3d8ff;
  }
  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
}

.listing-desc-preview {
  margin-top: 8px;
  /deep/ .el-textarea__inner {
    font-family: inherit;
    line-height: 1.65;
    font-size: 13px;
    color: #303133;
    background: #fafafa;
  }
  &--step /deep/ .el-textarea__inner {
    min-height: 280px;
    padding: 16px;
  }
}

.listing-desc-footer-block {
  margin-top: 16px;
  padding: 12px 14px;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 8px;

  &__label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 8px;
  }

  &__text {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 13px;
    line-height: 1.65;
    color: #606266;
    font-family: inherit;
  }

  &--preview {
    background: #fafafa;
    border-style: dashed;
  }
}

.desc-grade-tag {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #606266;
}

.publish-preview {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  background: #fafafa;
  border-radius: 8px;
  margin-bottom: 20px;
}
.publish-preview__score {
  flex-shrink: 0;
  text-align: center;
  min-width: 72px;
}
.publish-preview__level {
  display: block;
  font-size: 22px;
  font-weight: 700;
  color: #111;
}
.publish-preview__num {
  font-size: 12px;
  color: #909399;
}
.publish-preview__imgs {
  display: flex;
  gap: 6px;
  align-items: center;
  img {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #eee;
  }
}
.publish-preview__more {
  font-size: 12px;
  color: #909399;
}
.publish-form { max-width: 640px; }
.form-unit { margin-left: 8px; color: #909399; font-size: 13px; }

.step-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.grade-aside {
  width: 280px;
  flex-shrink: 0;
  position: sticky;
  top: 72px;
}
.aside-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.aside-extra {
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px solid #f0f0f0;
}
.aside-extra__title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
  line-height: 1.4;
}
.attr-form--aside {
  padding-top: 0;
  .attr-row {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    margin-bottom: 12px;
    &:last-child { margin-bottom: 0; }
  }
  .attr-label {
    width: auto;
    text-align: left;
    line-height: 1.4;
    font-size: 12px;
    color: #909399;
  }
  .attr-tag {
    padding: 3px 8px;
    font-size: 11px;
  }
}
.aside-score {
  text-align: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 12px;
}
.aside-score__level {
  font-size: 28px;
  font-weight: 700;
  color: #111;
  line-height: 1.2;
}
.aside-score__num {
  font-size: 14px;
  color: #606266;
  margin-top: 4px;
  small { color: #909399; }
}
.aside-summary {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  font-size: 11px;
  color: #606266;
  line-height: 1.6;
  max-height: 120px;
  overflow-y: auto;
  li { padding: 2px 0; }
}
.aside-summary__more { color: #909399; }
.aside-empty {
  font-size: 12px;
  color: #c0c4cc;
  text-align: center;
  margin: 0 0 12px;
}
.aside-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  /deep/ .el-button { margin: 0; width: 100%; }
}

@media (max-width: 960px) {
  .grade-aside { display: none; }
  .extra-mobile {
    display: block;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
  }
  .grade-layout { padding: 12px; }
}
.extra-mobile { display: none; }
</style>



// WEBPACK FOOTER //
// src/pages/consignmentGrade/index.vue